import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { TZDate } from '@date-fns/tz';
import { plural, t } from '@lingui/core/macro';
import { BookingRecordListForCustomer, BookingStatus, BrandRecordList } from '@navio/nsdk';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NavioTimeline, NoddiBadge, NoddiCard, NoddiLink } from 'noddi-ui-web';
import { BookingDetailsTabs, CustomDateFormats, customerRoutes, NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import type { ReactNode } from 'react';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';

// Same rule as the homepage's upcoming list: drafts and cancelled bookings are not "upcoming".
const ACTIVE_STATUSES = new Set<BookingStatus>([BookingStatus.CONFIRMED, BookingStatus.ASSIGNED_TO_ROUTE]);

// A few upcoming bookings so one that is still a draft or cancelled does not hide the next active one.
const UPCOMING_PAGE_SIZE = 5;

type Props = {
  userGroupId: number;
  carId: number;
};

/** The next active booking for this car; renders nothing when none is scheduled. */
export const UpcomingCarBooking = ({ userGroupId, carId }: Props) => {
  const { currentBrand } = useCurrentBrand();
  const {
    data: upcomingBookings,
    isPending,
    error
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedBookingsForCustomer,
    input: {
      userGroupId,
      isUpcoming: true,
      pageSize: UPCOMING_PAGE_SIZE,
      pageIndex: 0,
      brandDomains: [currentBrand],
      carIds: [carId]
    }
  });

  // No skeleton: most cars have no upcoming booking, so a placeholder would flash and vanish.
  if (isPending) {
    return null;
  }
  if (error) {
    return <ApiErrorMessageWithTrans error={error} />;
  }

  const booking = upcomingBookings.results.find(({ status }) => ACTIVE_STATUSES.has(status.value));

  // Only an actual upcoming booking earns the space; the header's Book button covers starting a new one.
  if (!booking) {
    return null;
  }

  return <UpcomingBookingCard booking={booking} carId={carId} />;
};

type UpcomingBookingCardProps = {
  booking: BookingRecordListForCustomer;
  carId: number;
};

const UpcomingBookingCard = ({ booking, carId }: UpcomingBookingCardProps) => {
  const { timezone } = booking.address;
  const services = booking.bookingItemsCar
    .filter((bookingItem) => bookingItem.id === carId)
    .flatMap((bookingItem) => bookingItem.orderLines?.map(({ description }) => description) ?? []);
  const date = noddiFormatDate(new TZDate(booking.date, timezone.value), CustomDateFormats.FULL_WEEKDAY_DAY_DOT_MONTH);
  const timeWindow = `${noddiFormatDate(new TZDate(booking.deliveryWindowStartsAt, timezone.value), NoddiDateFormats.TIME)}–${noddiFormatDate(new TZDate(booking.deliveryWindowEndsAt, timezone.value), NoddiDateFormats.TIME)}`;

  const steps = [
    { key: 'booked', title: t`Booked` },
    { key: 'confirmed', title: t`Confirmed` },
    // eslint-disable-next-line noddi/no-banned-ui-terms -- consumer copy: end customers read 'technician'; 'worker' is the backoffice term
    { key: 'onTheWay', title: t`Technician on the way` },
    { key: 'completed', title: t`Completed` }
  ];

  return (
    <NoddiCard
      contentClassName='flex flex-col gap-5'
      footer={
        <div className='flex flex-col gap-5'>
          {/* An upcoming booking is confirmed; it moves on once the technician starts the route. Four labels
              do not fit a phone row, so the strip is desktop-only. */}
          <NavioTimeline
            variant='bars'
            steps={steps}
            currentStep={booking.startedAt ? 2 : 1}
            className='max-md:hidden'
          />
          <NoddiLink
            to={customerRoutes.bookingInfo.getPath({ slug: booking.slug, tab: BookingDetailsTabs.ORDER_DETAILS })}
            endIcon='ArrowRight'
            className='self-end'
          >
            {t`See booking`}
          </NoddiLink>
        </div>
      }
    >
      <div className='flex flex-wrap items-start justify-between gap-4'>
        <div className='flex min-w-0 flex-1 basis-72 flex-col gap-2'>
          <span className='text-sm font-medium text-darkPurple70'>{t`Upcoming booking`}</span>
          <span className='text-2xl leading-tight font-bold tracking-tight text-primary first-letter:uppercase md:text-3xl'>
            {date}
          </span>
          {services.length > 0 && <span className='text-pretty text-darkPurple80'>{services.join(', ')}</span>}
        </div>
        <NoddiBadge withCircle>{relativeDayLabel(booking.date)}</NoddiBadge>
      </div>
      {/* Three facts side by side, split by hairlines that reach the card's edges like the footer band. */}
      <dl className='-mx-5 grid grid-cols-1 divide-y divide-border border-t border-border sm:grid-cols-3 sm:divide-x sm:divide-y-0'>
        <Fact label={t`Time window`} value={timeWindow} />
        <Fact label={t`Address`} value={`${booking.address.streetName} ${booking.address.streetNumber}`} />
        <Fact label={t`Provided by`} value={<ProviderLogo brand={booking.serviceDepartmentBrand} />} />
      </dl>
    </NoddiCard>
  );
};

type FactProps = {
  label: string;
  value: ReactNode;
};

const Fact = ({ label, value }: FactProps) => (
  <div className='flex min-w-0 flex-col gap-1 px-5 py-4'>
    <dt className='text-sm text-muted-foreground'>{label}</dt>
    <dd className='truncate font-medium'>{value}</dd>
  </div>
);

const ProviderLogo = ({ brand }: { brand: BrandRecordList }) =>
  brand.logo.url ? (
    <img src={brand.logo.url} alt={brand.name} className='h-5 w-auto max-w-24 object-contain' />
  ) : (
    <span>{brand.name}</span>
  );

const relativeDayLabel = (date: string) => {
  const days = differenceInCalendarDays(parseISO(date), new Date());
  if (days <= 0) {
    return t`Today`;
  }
  if (days === 1) {
    return t`Tomorrow`;
  }
  return plural(days, { one: 'In # day', other: 'In # days' });
};
